import React from "react";
import { Link } from "react-router-dom";
import { Book, CheckSquare, Calendar, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { getCardColor, calculateSemesterWeeks } from "../../../../utils/courseHelpers";

const CourseCard = ({ course, index = 0, showAssignmentCount = true, showSemesterWeeks = true, onCourseClick }) => {
  const cardColor = getCardColor(course.title || course.courseCode);
  const semesterWeeks = showSemesterWeeks
    ? calculateSemesterWeeks(course.semester?.startDate, course.semester?.endDate)
    : null;

  const cardContent = (
    <>
      {/* Color Banner with diagonal gradient cut */}
      <div
        className="relative h-20 sm:h-24 w-full overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${cardColor} 0%, ${cardColor} 60%, ${cardColor}cc 60%, ${cardColor}99 100%)`,
        }}
      >
        {/* Diagonal cut overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, transparent 50%, rgba(255,255,255,0.08) 50%)`,
          }}
        />

        {/* Course code */}
        <div className="absolute bottom-2.5 left-3 sm:left-4">
          <span className="text-white text-lg sm:text-xl font-bold tracking-wide drop-shadow-sm">
            {course.courseCode || "\u2014"}
          </span>
        </div>

        {/* Semester badge — sharp rectangle */}
        {course.semester?.name && (
          <span className="absolute top-2.5 right-2.5 px-2.5 py-0.5 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded-sm border border-white/10">
            {course.semester.name}
          </span>
        )}
      </div>

      {/* Course Details */}
      <div className="p-3 sm:p-4 flex-1">
        <h3 className="text-base font-bold text-gray-900 dark:text-white group-hover:text-accent1 dark:group-hover:text-accent1 transition-colors line-clamp-2">
          {course.title}
        </h3>
        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1.5 line-clamp-2">
          {course.aboutCourse}
        </p>
      </div>

      {/* Footer: stats + arrow */}
      <div className="px-3 sm:px-4 py-2.5 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center space-x-3">
            {showAssignmentCount && course.assignmentCount != null && (
              <div className="flex items-center space-x-1">
                <CheckSquare className="h-3 w-3" />
                <span>{course.assignmentCount} Assign.</span>
              </div>
            )}
            <div className="flex items-center space-x-1">
              <Book className="h-3 w-3" />
              <span>{course.lectureCount} Lec.</span>
            </div>
            {semesterWeeks && (
              <div className="flex items-center space-x-1">
                <Calendar className="h-3 w-3" />
                <span>{semesterWeeks} Wks</span>
              </div>
            )}
          </div>
          <ArrowRight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
    </>
  );

  const wrapperClassName = "group relative flex flex-col justify-between rounded-sm border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-none hover:shadow-md transition-all duration-200 overflow-hidden bg-white dark:bg-gray-800";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
      whileHover={{ y: -2 }}
    >
      {onCourseClick ? (
        <button
          type="button"
          onClick={() => onCourseClick(course._id)}
          className={`${wrapperClassName} w-full text-left cursor-pointer`}
        >
          {cardContent}
        </button>
      ) : (
        <Link
          to={`/student/course/${course._id}`}
          className={wrapperClassName}
        >
          {cardContent}
        </Link>
      )}
    </motion.div>
  );
};

export default CourseCard;
